// 三语术语库：TermTip 组件与 /glossary 术语表页共用
export type TermLang = 'en' | 'zh-Hant' | 'zh-Hans'
export interface TermDef {
  term: string
  short: string
  long?: string
}
const G: Record<string, Record<TermLang, TermDef>> = {
  fundingRate: {
    en: { term: 'Funding Rate', short: 'A periodic fee exchanged between long and short holders of a perpetual, paid long-to-short when positive.' },
    'zh-Hant': { term: '資金費率', short: '永續合約多空之間定期轉移的費用，正費率由多頭支付給空頭。' },
    'zh-Hans': { term: '资金费率', short: '永续合约多空之间定期转移的费用，正费率由多头支付给空头。' },
  },
  basis: {
    en: { term: 'Basis', short: 'The difference between futures price and spot price, reflecting market sentiment and holding cost.' },
    'zh-Hant': { term: '基差', short: '期貨價格與現貨價格之差，反映市場情緒與持倉成本。' },
    'zh-Hans': { term: '基差', short: '期货价格与现货价格之差，反映市场情绪与持仓成本。' },
  },
  openInterest: {
    en: { term: 'Open Interest', short: 'The total number of open derivative contracts, measuring how much capital is participating.' },
    'zh-Hant': { term: '持倉量', short: '市場中未平倉合約總數，衡量資金參與度。' },
    'zh-Hans': { term: '持仓量', short: '市场中未平仓合约总数，衡量资金参与度。' },
  },
  longShortRatio: {
    en: { term: 'Long/Short Ratio', short: 'The ratio of long positions to short positions in the market.' },
    'zh-Hant': { term: '多空比', short: '多方持倉與空方持倉之比。' },
    'zh-Hans': { term: '多空比', short: '多方持仓与空方持仓之比。' },
  },
  liquidation: {
    en: { term: 'Liquidation', short: 'Forced closing of a position when margin is insufficient.' },
    'zh-Hant': { term: '清算/強平', short: '保證金不足時由交易所強制平倉。' },
    'zh-Hans': { term: '清算/强平', short: '保证金不足时由交易所强制平仓。' },
  },
  perpetual: {
    en: { term: 'Perpetual', short: 'A futures contract with no expiry date, kept in line with spot via funding rates.' },
    'zh-Hant': { term: '永續合約', short: '無到期日的期貨合約，靠資金費率錨定現貨價格。' },
    'zh-Hans': { term: '永续合约', short: '无到期日的期货合约，靠资金费率锚定现货价格。' },
  },
  markPrice: {
    en: { term: 'Mark Price', short: 'A reference price used to compute PnL and liquidations, reducing manipulation.' },
    'zh-Hant': { term: '標記價格', short: '交易所用於計算盈虧與清算的參考價，降低價格操縱影響。' },
    'zh-Hans': { term: '标记价格', short: '交易所用于计算盈亏与清算的参考价，降低价格操纵影响。' },
  },
  unrealizedPnl: {
    en: { term: 'Unrealized PnL', short: 'Floating profit or loss of open positions at current market price.' },
    'zh-Hant': { term: '未實現盈虧', short: '當前持倉按市價計算的浮動盈虧。' },
    'zh-Hans': { term: '未实现盈亏', short: '当前持仓按市价计算的浮动盈亏。' },
  },
  realizedPnl: {
    en: { term: 'Realized PnL', short: 'Actual profit or loss booked after closing a position.' },
    'zh-Hant': { term: '已實現盈虧', short: '平倉後落袋的實際盈虧。' },
    'zh-Hans': { term: '已实现盈亏', short: '平仓后落袋的实际盈亏。' },
  },
  margin: {
    en: { term: 'Margin', short: 'Collateral locked to open and maintain a leveraged position.' },
    'zh-Hant': { term: '保證金', short: '開倉時鎖定的抵押資金。' },
    'zh-Hans': { term: '保证金', short: '开仓时锁定的抵押资金。' },
  },
  leverage: {
    en: { term: 'Leverage', short: 'A tool that magnifies position size using a small amount of margin.' },
    'zh-Hant': { term: '槓桿', short: '用少量保證金放大倉位倍數的工具。' },
    'zh-Hans': { term: '杠杆', short: '用少量保证金放大仓位倍数的工具。' },
  },
  volatility: {
    en: { term: 'Volatility', short: 'A measure of how much and how fast a price moves.' },
    'zh-Hant': { term: '波動率', short: '衡量價格變動幅度與速度的指標。' },
    'zh-Hans': { term: '波动率', short: '衡量价格变动幅度与速度的指标。' },
  },
  fearGreed: {
    en: { term: 'Fear & Greed Index', short: 'A 0-100 composite indicator of market sentiment.' },
    'zh-Hant': { term: '恐懼貪婪指數', short: '綜合衡量市場情緒的 0-100 指標。' },
    'zh-Hans': { term: '恐惧贪婪指数', short: '综合衡量市场情绪的 0-100 指标。' },
  },
  rsi: {
    en: { term: 'RSI', short: 'A 0-100 oscillator measuring recent buying vs selling strength.' },
    'zh-Hant': { term: '相對強弱指標', short: '衡量近期漲跌力度的 0-100 震盪指標。' },
    'zh-Hans': { term: '相对强弱指标', short: '衡量近期涨跌力度的 0-100 震荡指标。' },
  },
  movingAverage: {
    en: { term: 'Moving Average', short: 'The average price over a fixed period, used to observe trends.' },
    'zh-Hant': { term: '移動平均', short: '一定週期價格的平均線，用於觀察趨勢。' },
    'zh-Hans': { term: '移动平均', short: '一定周期价格的平均线，用于观察趋势。' },
  },
  percentile: {
    en: { term: 'Historical Percentile', short: 'Where the current value ranks within its historical range, in percent.' },
    'zh-Hant': { term: '歷史分位', short: '當前值在歷史區間中的百分位排名。' },
    'zh-Hans': { term: '历史分位', short: '当前值在历史区间中的百分位排名。' },
  },
  fundingCost: {
    en: { term: 'Funding Cost', short: 'Cumulative funding fees paid or received while holding a position.' },
    'zh-Hant': { term: '資金成本', short: '持倉期間累計支付或收取的資金費。' },
    'zh-Hans': { term: '资金成本', short: '持仓期间累计支付或收取的资金费。' },
  },
  grid: {
    en: { term: 'Grid Trading', short: 'Placing laddered buy/sell orders within a price range to profit from oscillation.' },
    'zh-Hant': { term: '網格交易', short: '在價格區間按等差/等比掛單低買高賣。' },
    'zh-Hans': { term: '网格交易', short: '在价格区间按等差/等比挂单低买高卖。' },
  },
  backtest: {
    en: { term: 'Backtest', short: 'Evaluating a strategy against historical data.' },
    'zh-Hant': { term: '回測', short: '用歷史數據驗證策略表現。' },
    'zh-Hans': { term: '回测', short: '用历史数据验证策略表现。' },
  },
  paperTrading: {
    en: { term: 'Paper Trading', short: 'Trading with virtual capital to practice without real risk.' },
    'zh-Hant': { term: '模擬盤', short: '用虛擬資金模擬實盤交易，無真實風險。' },
    'zh-Hans': { term: '模拟盘', short: '用虚拟资金模拟实盘交易，无真实风险。' },
  },
}
export function glossaryOf(key: string): Record<TermLang, TermDef> | undefined {
  return G[key]
}
export const GLOSSARY_KEYS = Object.keys(G)
export default G
